import { Suspense, useCallback, useMemo, useState } from 'react';

import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { useQueryString } from '@hivebrite/react-hooks';

import { useStore } from './store';
import oneColumnTemplate from './templates/OneColumnTemplate';
import { byZone } from './templates/utils';

const dataSelector = (state) => state.data;
const setBlockDataSelector = (state) => state.setBlockData;
const setDataSelector = (state) => state.setData;

const Block = ({
  Component,
  blockId,
  block,
  dataTestId,
  templateId,
  setScreenReaderStatus,
}) => {
  const data = useStore(dataSelector);
  const blockData = useStore(
    useCallback((state) => state.data[blockId], [blockId]),
  );
  const setBlockData = useStore(setBlockDataSelector);
  const setData = useStore(setDataSelector);
  const [query, setQuery] = useQueryString();

  const handleSetBlockData = useCallback(
    (dataOrFn) => setBlockData(blockId, dataOrFn),
    [blockId, setBlockData],
  );

  const blockQuery = query[blockId];
  const setBlockQuery = useCallback(
    (blockQueryOrFunc) =>
      setQuery((prevQuery) => {
        if (typeof blockQueryOrFunc === 'function') {
          const prevBlockQuery = prevQuery[blockId] ?? {};
          const newBlockQuery = blockQueryOrFunc(prevBlockQuery);

          return {
            ...prevQuery,
            [blockId]: newBlockQuery,
          };
        }

        return {
          ...prevQuery,
          [blockId]: blockQueryOrFunc,
        };
      }),
    [blockId, setQuery],
  );

  return (
    <Component
      blockData={blockData}
      blockQuery={blockQuery}
      blockId={blockId}
      data={data}
      ref={block.ref}
      setBlockData={handleSetBlockData}
      setBlockQuery={setBlockQuery}
      setData={setData}
      dataTestId={dataTestId}
      setScreenReaderStatus={setScreenReaderStatus}
      // This will clear the search params, reseting any filter that was applied
      clearFilters={() => setQuery()}
      {...(block.attributes || {})}
      {...(block.templateAttributes?.[templateId] || {})}
    />
  );
};

const BlocksRenderer = ({
  blocks,
  templateId = oneColumnTemplate.id,
  templateComponent: Template = oneColumnTemplate.component,
  templateZones = oneColumnTemplate.zones,
  templateProps,
}) => {
  const data = useStore(dataSelector);

  const [screenReaderStatus, setScreenReaderStatus] = useState('');

  const blocksList = useMemo(
    () =>
      blocks.filter(Boolean).reduce((result, item, index) => {
        const Component = typeof item === 'function' ? item : item.component;

        if (Component) {
          const blockData = {
            block: item,
            blockId: item.id || index,
            Component,
          };

          result.push({
            blockId: blockData.blockId,
            block: (
              <Block
                key={blockData.blockId}
                dataTestId={item?.id}
                templateId={templateId}
                setScreenReaderStatus={setScreenReaderStatus}
                {...blockData}
              />
            ),
          });
        }

        return result;
      }, []),
    [blocks, templateId],
  );

  const zonesBlocks = useMemo(
    () => byZone(blocksList, templateZones),
    [blocksList, templateZones],
  );

  return (
    <Suspense fallback={null}>
      <ScreenReadersContent aria-live="polite" role="status">
        {screenReaderStatus}
      </ScreenReadersContent>
      <Template zones={zonesBlocks} data={data} {...templateProps} />
    </Suspense>
  );
};

export default BlocksRenderer;
